/**
 * SB6-ANA-CACHE-03 — LRU eviction by WHOLE dataset.
 *
 * The cache budget caps the UNPINNED footprint two independent ways
 * (`storageBudget`): a byte ceiling (default 10 GB) AND a dataset-count ceiling
 * (default 3) — whichever trips first. When an unpinned dataset's download
 * pushes the unpinned total over either ceiling, this evictor removes whole
 * least-recently-accessed unpinned datasets (cache entries + manifest record)
 * until both ceilings are satisfied.
 *
 * ## Invariants (story §Scope / AC2 / AC3 / AC7)
 * - **Pinned datasets are exempt** — never evicted, never counted against the cap
 *   (but they still occupy real storage, surfaced in the UI's total).
 * - **Whole-dataset only** — a half-evicted dataset is the worst of both worlds;
 *   we delete a dataset's entire key set or none of it.
 * - **Never evict the currently-open acquisition** — it is treated as
 *   most-recently-accessed and skipped even when unpinned (AC7). The open id is
 *   supplied by the caller (the prefetch scheduler's bound dataset).
 * - **LRU order** — candidates are sorted by ascending `lastAccess`; the oldest
 *   evictable dataset goes first.
 *
 * Eviction is best-effort and asynchronous (cache deletes are async); it never
 * throws. A run is serialised — a second trigger while one is in flight is
 * coalesced so we don't double-evict.
 */
import { deleteKeys } from "./imageCache.js";
import { cacheManifest, type DatasetManifest } from "./cacheManifest.js";
import { storageBudget } from "./storageBudget.js";

/** Result of one eviction pass (for tests + diagnostics). */
export interface EvictionResult {
  /** Acquisition ids removed, in eviction order. */
  evicted: string[];
}

/**
 * Choose the set of datasets to evict to bring the unpinned footprint under
 * BOTH budgets, honouring pin-exemption and the open-acquisition skip. Pure:
 * no side effects, operates on a snapshot. Exported for unit testing the policy
 * in isolation from the async cache deletes.
 */
export function selectEvictions(
  manifests: readonly DatasetManifest[],
  budget: { maxBytes: number; maxUnpinnedDatasets: number },
  openAcquisitionId: string | null,
): DatasetManifest[] {
  // Evictable = unpinned AND not the open acquisition. Sorted LRU (oldest first).
  const evictable = manifests
    .filter((m) => !m.pinned && m.dataAcquisitionId !== openAcquisitionId)
    .sort((a, b) => a.lastAccess - b.lastAccess);

  // Current unpinned totals (the open acquisition still COUNTS toward the budget
  // — it just can't be the one we evict; we evict others to make room for it).
  let unpinnedBytes = 0;
  let unpinnedCount = 0;
  for (const m of manifests) {
    if (m.pinned) continue;
    unpinnedBytes += m.totalBytes;
    unpinnedCount += 1;
  }

  const overBudget = (): boolean =>
    unpinnedBytes > budget.maxBytes ||
    unpinnedCount > budget.maxUnpinnedDatasets;

  const chosen: DatasetManifest[] = [];
  for (const candidate of evictable) {
    if (!overBudget()) break;
    chosen.push(candidate);
    unpinnedBytes -= candidate.totalBytes;
    unpinnedCount -= 1;
  }
  return chosen;
}

let evictionInFlight: Promise<EvictionResult> | null = null;

/** One eviction pass against the CURRENT manifest snapshot. */
async function runEvictionPass(
  openAcquisitionId: string | null,
): Promise<EvictionResult> {
  const evicted: string[] = [];
  try {
    const chosen = selectEvictions(
      cacheManifest.list(),
      storageBudget.getBudget(),
      openAcquisitionId,
    );
    for (const dataset of chosen) {
      // Delete the WHOLE dataset's image bytes, then its manifest record.
      await deleteKeys(dataset.imageKeys);
      cacheManifest.removeDataset(dataset.dataAcquisitionId);
      evicted.push(dataset.dataAcquisitionId);
    }
  } catch {
    /* best-effort — partial progress is fine, never throw into the queue */
  }
  return { evicted };
}

/**
 * Run eviction. Serialised, but NOT coalesced into a single stale pass: a call
 * submitted while one is running CHAINS a fresh pass after it (the manifest may
 * have changed between submission and execution — e.g. a download recorded more
 * datasets). The returned promise resolves to the result of the pass that ran
 * for THIS call, so callers (and tests) get an up-to-date evaluation.
 * `openAcquisitionId` is never evicted (AC7).
 */
export function runEviction(
  openAcquisitionId: string | null,
): Promise<EvictionResult> {
  const prior =
    evictionInFlight ??
    Promise.resolve<EvictionResult>({
      evicted: [],
    });
  const next = prior
    .catch(() => ({ evicted: [] as string[] }))
    .then(() => runEvictionPass(openAcquisitionId));
  evictionInFlight = next.finally(() => {
    if (evictionInFlight === next) evictionInFlight = null;
  });
  return next;
}

/**
 * Fire-and-forget eviction trigger for the hot path (imageCache.put / prefetch
 * completion). Doesn't await — the caller's `put` resolves immediately and the
 * eviction settles in the background.
 */
export function triggerEviction(openAcquisitionId: string | null): void {
  void runEviction(openAcquisitionId);
}
